/**
 * Banglish → Bangla transliteration.
 *
 * Rule-based phonetic mapping tuned for the common automotive-parts
 * vocabulary Bangladeshi shop owners type in Roman script
 * ("break pad", "engine oil", "toyota axio er hedlight"). The mapping is
 * intentionally conservative — we prefer to leave the token untouched
 * when we're not confident, so vendors always see something they can
 * lightly edit rather than a scrambled string.
 *
 * Two entry points:
 *   - `transliterateBanglish(text)` — best-effort word-by-word conversion.
 *     Common whole words (brand/part vocabulary) are looked up in a
 *     dictionary first; unknown tokens fall through to the phonetic table.
 *   - `looksBangla(text)` — true when the string already contains Bangla
 *     glyphs (U+0980–U+09FF), so the wizard can skip the assist button.
 *
 * NOT a substitute for a proper transliteration engine — this exists so a
 * vendor can go from "brake pad toyota axio" to "ব্রেক প্যাড টয়োটা অ্যাক্সিও"
 * in one tap and refine from there.
 */

// Whole-word overrides for high-frequency vocabulary. Keys are lowercased.
const WORD_DICT: Record<string, string> = {
  // Vehicle brands
  toyota: "টয়োটা",
  honda: "হোন্ডা",
  nissan: "নিসান",
  suzuki: "সুজুকি",
  mitsubishi: "মিতসুবিশি",
  hyundai: "হুন্দাই",
  kia: "কিয়া",
  ford: "ফোর্ড",
  mazda: "মাজদা",
  bmw: "বিএমডব্লিউ",
  bajaj: "বাজাজ",
  yamaha: "ইয়ামাহা",
  tvs: "টিভিএস",
  hero: "হিরো",
  // Part brands
  bosch: "বোশ",
  ngk: "এনজিকে",
  denso: "ডেনসো",
  // Common parts
  brake: "ব্রেক",
  pad: "প্যাড",
  disc: "ডিস্ক",
  clutch: "ক্লাচ",
  engine: "ইঞ্জিন",
  oil: "অয়েল",
  filter: "ফিল্টার",
  air: "এয়ার",
  fuel: "ফুয়েল",
  spark: "স্পার্ক",
  plug: "প্লাগ",
  battery: "ব্যাটারি",
  headlight: "হেডলাইট",
  tail: "টেইল",
  light: "লাইট",
  mirror: "মিরর",
  bumper: "বাম্পার",
  tyre: "টায়ার",
  tire: "টায়ার",
  tube: "টিউব",
  belt: "বেল্ট",
  gasket: "গ্যাসকেট",
  radiator: "রেডিয়েটর",
  shock: "শক",
  absorber: "অ্যাবজরবার",
  bearing: "বেয়ারিং",
  // Connector words
  er: "এর",
  for: "ফর",
  and: "এবং",
  the: "",
  // Common models
  axio: "অ্যাক্সিও",
  corolla: "করোলা",
  premio: "প্রিমিও",
  allion: "অ্যালিয়ন",
  civic: "সিভিক",
  city: "সিটি",
  // Condition / part-type words
  new: "নতুন",
  used: "ব্যবহৃত",
  genuine: "জেনুইন",
  oem: "ওইএম",
  aftermarket: "আফটারমার্কেট",
  // Units
  set: "সেট",
  pcs: "পিস",
  piece: "পিস",
};

// Multi-character digraphs must be tried before single letters.
const DIGRAPHS: Array<[string, string]> = [
  ["chh", "ছ"],
  ["shh", "ষ"],
  ["kkh", "ক্ষ"],
  ["ngg", "ঙ্গ"],
  ["ksh", "ক্ষ"],
  ["gyo", "জ্ঞ"],
  ["oii", "ৈ"],
  ["ou", "ৌ"],
  ["oo", "ু"],
  ["ee", "ী"],
  ["ai", "ৈ"],
  ["au", "ৌ"],
  ["kh", "খ"],
  ["gh", "ঘ"],
  ["ch", "চ"],
  ["jh", "ঝ"],
  ["th", "থ"],
  ["dh", "ধ"],
  ["ph", "ফ"],
  ["bh", "ভ"],
  ["sh", "শ"],
  ["ng", "ং"],
  ["rr", "র"],
];

const SINGLES: Record<string, string> = {
  a: "া",
  i: "ি",
  u: "ু",
  e: "ে",
  o: "ো",
  k: "ক",
  g: "গ",
  j: "জ",
  t: "ট",
  d: "ড",
  n: "ন",
  p: "প",
  b: "ব",
  m: "ম",
  y: "য়",
  r: "র",
  l: "ল",
  s: "স",
  h: "হ",
  w: "ওয়",
  f: "ফ",
  v: "ভ",
  z: "জ",
  q: "ক",
  x: "ক্স",
  c: "স",
};

// Consonants get an implicit "অ" vowel — when a token starts with one
// (no leading vowel), we render the consonant glyph without a matra.
const VOWEL_STARTS: Record<string, string> = {
  a: "আ",
  i: "ই",
  u: "উ",
  e: "এ",
  o: "ও",
};

/** True if the string contains any Bangla codepoint. */
export function looksBangla(text: string): boolean {
  return /[\u0980-\u09FF]/.test(text);
}

/**
 * Convert a single Banglish word (already lowercased, ASCII letters) into
 * a best-effort Bangla rendering. Unknown characters pass through
 * untouched so the caller sees where the mapping failed.
 */
function convertWord(word: string): string {
  if (!word) return word;
  const dict = WORD_DICT[word];
  if (dict !== undefined) return dict;

  let out = "";
  let i = 0;
  let firstLetter = true;

  while (i < word.length) {
    // Digraph lookup
    let matched = false;
    for (const [key, val] of DIGRAPHS) {
      if (word.startsWith(key, i)) {
        out += val;
        i += key.length;
        matched = true;
        firstLetter = false;
        break;
      }
    }
    if (matched) continue;

    const ch = word[i];
    if (firstLetter && ch in VOWEL_STARTS) {
      out += VOWEL_STARTS[ch];
      firstLetter = false;
    } else if (ch in SINGLES) {
      out += SINGLES[ch];
      firstLetter = false;
    } else {
      out += ch;
    }
    i += 1;
  }
  return out;
}

/**
 * Best-effort convert a mixed Roman/Bangla string into Bangla. Existing
 * Bangla tokens, digits and punctuation are preserved verbatim.
 */
export function transliterateBanglish(text: string): string {
  if (!text) return "";
  // Split preserving whitespace/punctuation as separators.
  return text
    .split(/(\s+|[^\p{L}\p{N}']+)/u)
    .map((tok) => {
      if (!tok) return tok;
      if (looksBangla(tok)) return tok;
      if (!/^[A-Za-z']+$/.test(tok)) return tok;
      return convertWord(tok.toLowerCase());
    })
    .join("");
}

/**
 * Convert ASCII digits (0-9) inside a string into Bangla numerals.
 * Useful for a "৳1,800 = ৳১,৮০০" preview beside price inputs.
 */
export function toBanglaNumerals(text: string): string {
  const map: Record<string, string> = {
    "0": "০",
    "1": "১",
    "2": "২",
    "3": "৩",
    "4": "৪",
    "5": "৫",
    "6": "৬",
    "7": "৭",
    "8": "৮",
    "9": "৯",
  };
  return text.replace(/[0-9]/g, (d) => map[d] ?? d);
}

// Reverse lookup: Bangla word → Roman equivalent, built from WORD_DICT.
// Used by search to cross-match Bengali queries against English product data.
const REVERSE_WORD_DICT: Record<string, string> = (() => {
  const out: Record<string, string> = {};
  for (const [roman, bangla] of Object.entries(WORD_DICT)) {
    if (bangla && !(bangla in out)) out[bangla] = roman;
  }
  return out;
})();

/**
 * Best-effort convert a Bangla string into Roman by looking up known
 * vocabulary word-by-word. Unknown tokens pass through untouched so the
 * search layer can still `ilike` them against Bangla columns. Returns
 * `null` if no known token was substituted (nothing useful to add).
 */
export function romanizeKnownBangla(text: string): string | null {
  if (!text) return null;
  let hit = false;
  const out = text
    .split(/(\s+|[^\p{L}\p{N}']+)/u)
    .map((tok) => {
      const key = tok.trim();
      if (!key) return tok;
      const roman = REVERSE_WORD_DICT[key];
      if (roman) {
        hit = true;
        return tok.replace(key, roman);
      }
      return tok;
    })
    .join("");
  return hit ? out : null;
}
